import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, CircularProgress, Grid, Card, CardContent, Button } from '@mui/material';
import { getProcessingQueue } from '../api';
import { useDemo, fakeQueueList } from '../demoMode';   // [D-431]
import { usePartnerIndex, PartnerTag } from '../components/partnerTag';
import MaintCard from '../components/MaintCard';   // [D-817] технічна перерва   // [D-770] та сама позначка, що в інших списках

// Кольори воркерів. Імена — ті, що celery_tasks.py пише у processing_queue.worker
// (за hostname: test-server -> worker-2, staging -> staging, n8n -> n8n-server,
// решта -> реальний hostname). Звірено 19.08.2026: worker-1/video-worker-1 більше
// НЕ існують (сервер видалено 10.07), натомість зʼявились worker-5 і netcup-1 —
// без них їхні задачі малювались сірим за замовчуванням.
// Палітра збігається з вкладкою «Памʼять AI», щоб один сервер не мав двох кольорів.
const WorkerColor = {
  'n8n-server': '#818cf8',
  'worker-2':   '#fb923c',
  'worker-5':   '#facc15',
  'staging':    '#4ade80',
  'netcup-1':   '#9ca3af',
  'phone':      '#f472b6',   // [D-731] обробка в застосунку на телефоні
  'pc':         '#22d3ee',   // [D-731] обробка в застосунку на ПК
};
const DeviceLabel = { phone: '📱 Телефон', pc: '💻 ПК' };
// [D-731] стан обробки на пристрої: live - застосунок шле «обробляю» (кожні ~15 с); lost - замовк > 2 хв; run - старий застосунок
// (сигналу немає - видно лише старт і готові версії)
const deviceState = (d) => {
  if (!d) return null;
  if (d.state === 'live') return { t: '🟢 на зв\'язку · ' + formatTime(d.quiet_sec) + ' тому', c: '#4caf50' };
  if (d.state === 'lost') return { t: '⚠️ зв\'язок втрачено ' + formatTime(d.quiet_sec) + ' тому', c: '#ffb74d' };
  return { t: '⏳ остання звістка ' + formatTime(d.quiet_sec) + ' тому', c: '#9a86ff' };
};

const formatTime = (sec) => {
  if (!sec) return '0с';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}хв ${s}с` : `${s}с`;
};

export default function QueuePage() {
  const [queue, setQueue] = useState([]);
  const demo = useDemo();
  const navigate = useNavigate();
  const tok = localStorage.getItem('dashboard_token');
  // [D-728] OWNER 25.10: «ще одну чергу… де буде показувати які юзери яких партнерів зараз обробляють відео» і «натиснути на ім'я…
  // щоб переходила у то меню… (Користувачі), обравши конкретного користувача». Черга (processing_queue.user_id = users.id) не знає
  // партнера - беремо його зі списку юзерів (users.partner_id) і назву зі списку партнерів; ці списки - раз на хвилину, не кожні 3 с.
  const [tab, setTab] = useState('all');
  // [D-770] «хто чий» - спільний partnerTag: одна легка ручка на всі списки,
  // кеш 5 хв. Раніше тут щохвилини тягнувся ПОВНИЙ /users з агрегатами по
  // відео заради двох полів - на сторінці, що оновлюється кожні 3 секунди.
  const { of, color, nameOf } = usePartnerIndex();

  // 🔴 У демо-режимі кнопки НЕ ходять на сервер. Ця сторінка вміє
  // зупиняти чужі задачі й чистити всю чергу — а показують її сторонній
  // людині. Один необережний клік під час демонстрації обірвав би
  // обробку реальним юзерам, і ніхто б не зрозумів чому.
  const clearQueue = async () => {
    if (!window.confirm('Очистити всю чергу?')) return;
    if (demo) { setQueue([]); return; }
    await fetch('/api/queue/clear', { method:'DELETE', headers:{ Authorization:'Bearer '+tok } });
    setQueue([]);
  };
  const stopTask = async (taskId) => {
    if (!window.confirm('Зупинити цю задачу?')) return;
    if (demo) { setQueue(q => q.filter(x => x.task_id !== taskId)); return; }
    await fetch('/api/queue/'+taskId, { method:'DELETE', headers:{ Authorization:'Bearer '+tok } });
    setQueue(q => q.filter(x => x.task_id !== taskId));
  };
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    // [D-431] У демо список беремо з генератора, сервер не турбуємо:
    // зайві запити на демонстрації ні до чого, та й показати треба не
    // те, що є, а те, що справляє враження.
    const load = demo
      ? () => { setQueue(fakeQueueList()); setLoading(false); }
      : () => {
          getProcessingQueue().then(r => { setQueue(r.data); setLoading(false); });
        };
    window._reloadQueue = load;
    load();
    const interval = setInterval(() => { load(); setTick(t => t + 1); }, 3000);
    return () => clearInterval(interval);
  }, [demo]);

  if (loading) return <Box sx={{ display:'flex', justifyContent:'center', mt:10 }}><CircularProgress sx={{ color:'var(--ac1, #7c4dff)' }}/></Box>;

  // [D-728] учні партнерів (у т. ч. сам партнер, коли він учень свого партнера) - у демо їх немає
  const pidOf = (item) => (demo ? null : ((of(item.user_id) || {}).pid || null));   // [D-770]
  const partnerItems = queue.filter(pidOf);
  const groups = {};
  partnerItems.forEach(it => { (groups[pidOf(it)] = groups[pidOf(it)] || []).push(it); });
  const openUser = (item) => { if (!demo) navigate(`/users/${item.user_id}`); };

  const renderItem = (item) => {
    const pid = pidOf(item);
    const dv = item.device;   // [D-731] обробка на пристрої юзера (не на сервері)
    const ds = deviceState(dv);
    return (
      <Grid item xs={12} md={6} key={item.task_id}>
        <Card sx={{ bgcolor:'#1a1a2e', border:`1px solid ${WorkerColor[item.worker] || '#333'}`, borderRadius:2 }}>
          <CardContent>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:1.5, flexWrap:'wrap' }}>
              {/* [D-728] ім'я - перехід на сторінку юзера з «Користувачів» */}
              <Typography variant="subtitle1" onClick={() => openUser(item)} title={demo ? '' : 'Відкрити все про юзера'}
                sx={{ color:'#fff', fontWeight:'bold', cursor: demo ? 'default' : 'pointer', minWidth:0, wordBreak:'break-word',
                      '&:hover': demo ? {} : { color:'var(--ac2, #a78bfa)', textDecoration:'underline' } }}>
                👤 {item.username ? (item.username.startsWith('@') ? item.username : '@'+item.username) : '?'} (id:{item.user_id})
              </Typography>
              <Box sx={{ display:'flex', gap:1, alignItems:'center', flexWrap:'wrap' }}>
                <Chip label={DeviceLabel[item.worker] || item.worker} size="small"
                  sx={{ bgcolor:`${WorkerColor[item.worker]}22`, color: WorkerColor[item.worker] || '#aaa', fontWeight: dv ? 'bold' : 'normal' }}/>
                {!dv && <Chip label="⏹ Зупинити" size="small" onClick={() => stopTask(item.task_id)}
                  sx={{ bgcolor:'rgba(244,67,54,0.2)', color:'#f44', cursor:'pointer' }}/>}
              </Box>
            </Box>
            <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'center', mb:1.2 }}>
              {!demo && <Chip data-open-user="1" label="📂 Уся інформація юзера →" size="small" onClick={() => openUser(item)}
                sx={{ bgcolor:'rgba(0,229,160,0.15)', color:'#7ffcd3', border:'1px solid rgba(0,229,160,0.5)', cursor:'pointer', fontWeight:'bold' }}/>}
              <PartnerTag who={of(item.user_id)} color={color} h={20}/>{/* [D-770] та сама позначка, що в «Користувачах» */}
              {/* [D-741] стан зв'язку - в одному рядку з «Уся інформація юзера», щоб картки були одного розміру */}
              {ds && <Typography data-device-state={dv.state} variant="caption" sx={{ color: ds.c, fontWeight:'bold', ml:'auto' }}>{ds.t}</Typography>}
            </Box>
            <Grid container spacing={1}>
              {(dv ? [
                { l:'⏱ Тривалість', v: item.duration_sec ? Math.round(item.duration_sec) + 'с' : '?' },
                { l:'📍 Де', v: DeviceLabel[item.worker] + ' юзера' },
                { l:'🎬 Готово версій', v: (dv.done || 0) + ' з ' + (dv.total || item.versions || '?') + (dv.pct != null ? ' · ' + dv.pct + '%' : '') },
                { l:'⚙️ Крок', v: dv.stage || (dv.live ? '-' : 'застосунок не повідомляє') },
                { l:'🕐 Обробляється', v: formatTime(item.elapsed_sec) },
              ] : [
                { l:'⏱ Тривалість', v: item.duration_sec ? Math.round(item.duration_sec) + 'с' : '?' },
                { l:'📐 Чіткість', v: item.resolution || '?' },
                // [D-741] хід обробки бота - з замірів після кожної готової версії
                { l:'🎬 Готово версій', v: item.progress ? item.progress.done + ' з ' + item.progress.total + ' · ' + item.progress.pct + '%' : (item.versions || 1) },
                { l:'⚡ Режим', v: item.mode === 'instant' ? '⚡ Миттєва' : item.mode === 'prof' ? '⚙️ Професійна' : '🎬 Стандартна' },
                { l:'🕐 Обробляється', v: formatTime(item.elapsed_sec) },
              ]).map(row => (
                <Grid item xs={6} key={row.l}>
                  <Box sx={{ p:0.8, bgcolor:'rgba(255,255,255,0.03)', borderRadius:1 }}>
                    <Typography variant="caption" sx={{ color:'#888', display:'block' }}>{row.l}</Typography>
                    <Typography variant="body2" sx={{ color:'#fff', fontWeight:'bold' }}>{row.v}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const shown = tab === 'all' ? queue : partnerItems;
  return (
    <Box>
      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2, flexWrap:'wrap' }}>
        <Typography variant="h5" sx={{ color:'#fff', fontWeight:'bold' }}>🎬 Обробка в реальному часі</Typography>
        <Chip label={`${queue.length} активних`} sx={{ bgcolor: queue.length > 0 ? 'rgba(76,175,80,0.8)' : 'rgba(26,26,46,0.8)', color: queue.length > 0 ? '#fff' : '#aaa', border:'1px solid '+(queue.length > 0 ? '#4caf50' : '#555') }}/>
        <Chip label="🔄 3 сек" sx={{ bgcolor:'rgba(26,26,46,0.8)', color:'#2196f3', border:'1px solid #2196f3' }}/>
        <Button size="small" variant="outlined" onClick={clearQueue}
          sx={{ borderColor:'#f44', color:'#f44', ml:'auto' }}>🗑 Очистити чергу</Button>
      </Box>

      <MaintCard />{/* [D-817] */}

      {/* [D-728] дві черги кнопками-вкладками: усі обробки і окремо - учні партнерів, по партнерах */}
      <Box sx={{ display:'flex', gap:1, mb:2.5, flexWrap:'wrap' }}>
        {[['all', `🎬 Усі обробки · ${queue.length}`], ['partners', `🤝 Учні партнерів · ${partnerItems.length}`]].map(([k, l]) => (
          <Button key={k} data-qtab={k} size="small" onClick={() => setTab(k)} variant={tab === k ? 'contained' : 'outlined'}
            sx={{ textTransform:'none', borderRadius:5, px:2, fontWeight:'bold',
                  bgcolor: tab === k ? 'var(--ac1, #7c4dff)' : 'transparent', color: tab === k ? '#fff' : 'var(--ac2, #a78bfa)',
                  borderColor:'var(--ac1, #7c4dff)' }}>{l}</Button>
        ))}
      </Box>

      {shown.length === 0 ? (
        <Box sx={{ textAlign:'center', p:3 }}><Typography sx={{ color:'#888', fontSize:16, bgcolor:'rgba(26,26,46,0.8)', px:3, py:1.5, borderRadius:2, display:'inline-block' }}>
          {tab === 'all' ? '😴 Зараз нічого не обробляється' : '😴 Учні партнерів зараз нічого не обробляють'}</Typography></Box>
      ) : tab === 'all' ? (
        <Grid container spacing={2}>{queue.map(renderItem)}</Grid>
      ) : (
        Object.keys(groups).map(pid => (
          <Paper key={pid} data-qpartner={pid} sx={{ bgcolor:'rgba(255,255,255,0.03)', border:`1px solid ${color(pid)}`, borderRadius:2, p:{ xs:1.5, sm:2 }, mb:2 }}>
            <Typography sx={{ color: color(pid), fontWeight:'bold', fontSize:16, mb:1.5 }}>
              🤝 {nameOf(pid)} · обробляють: {groups[pid].length}
            </Typography>
            <Grid container spacing={2}>{groups[pid].map(renderItem)}</Grid>
          </Paper>
        ))
      )}
    </Box>
  );
}
